import { createPortal } from "react-dom";
import { useCallback, useId, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { TruncatedText } from "@/components/ui";
import {
  FILTER_STATUS_DOT_CLASS,
  type FilterStatusDot,
} from "@/lib/statusDot";
import { chipClass } from "@/components/ui/chip-variants";
import { useAnchoredPanel } from "@/components/ui/overlay";
import { getModalPortalOwnerId } from "@/components/ui/overlay";

export interface AdminFilterOption<T> {
  value: T;
  label: string;
  count?: number;
  icon?: LucideIcon;
  iconBadgeClassName?: string;
  statusDot?: FilterStatusDot;
  dotClassName?: string;
}

interface AdminFilterMenuBaseProps<T> {
  icon?: LucideIcon;
  name: string;
  options: AdminFilterOption<T>[];
  className?: string;
  /** Extra classes on the chip trigger (e.g. joined filter groups). */
  triggerClassName?: string;
}

type AdminFilterMenuProps<T> =
  | (AdminFilterMenuBaseProps<T> & {
      mode?: "single";
      value: T;
      defaultValue: T;
      onChange: (value: T) => void;
    })
  | (AdminFilterMenuBaseProps<T> & {
      mode: "multiple";
      value: T[];
      onChange: (value: T[]) => void;
      anyLabel: string;
      countLabel: (count: number) => string;
    });

function optionKey<T>(value: T): string {
  if (value === null) {
    return "null";
  }
  return String(value);
}

function isValueSelected<T>(selected: T[], value: T): boolean {
  return selected.some((item) => Object.is(item, value));
}

type FilterOptionVisualFields = Pick<
  AdminFilterOption<never>,
  "icon" | "iconBadgeClassName" | "statusDot" | "dotClassName"
>;

function FilterOptionVisual({
  option,
  compact,
  reserveSpace,
}: {
  option: FilterOptionVisualFields;
  compact?: boolean;
  reserveSpace?: boolean;
}) {
  const Icon = option.icon;
  const resolvedDotClass =
    option.dotClassName ??
    (option.statusDot ? FILTER_STATUS_DOT_CLASS[option.statusDot] : undefined);
  const dotSize = compact ? "h-2 w-2" : "h-2.5 w-2.5";
  const iconSize = compact ? "h-3.5 w-3.5" : "h-4 w-4";
  const badgeSize = compact ? "h-5 w-5" : "h-6 w-6";

  if (resolvedDotClass) {
    const dot = (
      <span
        className={cn("shrink-0 rounded-full", dotSize, resolvedDotClass)}
        aria-hidden
      />
    );
    if (!compact) {
      return dot;
    }
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full bg-surface-0",
          badgeSize,
        )}
        aria-hidden
      >
        {dot}
      </span>
    );
  }

  if (!Icon) {
    if (reserveSpace) {
      return (
        <span
          className={cn("shrink-0", compact ? "w-5" : "w-6")}
          aria-hidden
        />
      );
    }
    return null;
  }

  if (option.iconBadgeClassName) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center",
          compact ? "rounded-full bg-surface-0" : "rounded-md",
          badgeSize,
          option.iconBadgeClassName,
          compact && "bg-surface-0",
        )}
      >
        <Icon
          className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5")}
          aria-hidden
        />
      </span>
    );
  }

  return (
    <Icon
      className={cn("shrink-0", iconSize)}
      aria-hidden
    />
  );
}

function optionRowClass(isSelected: boolean): string {
  return cn(
    "flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors",
    isSelected
      ? "bg-primary-50 font-medium text-primary-700"
      : "text-surface-700 hover:bg-surface-100",
  );
}

export function AdminFilterMenu<T>(props: AdminFilterMenuProps<T>) {
  const { icon: Icon, name, options, className, triggerClassName } = props;
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [modalPortalOwnerId, setModalPortalOwnerId] = useState<string>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setIsOpen(false), []);
  const toggleMenu = useCallback(() => {
    setIsOpen((previous) => {
      if (previous) {
        return false;
      }
      setModalPortalOwnerId(getModalPortalOwnerId(triggerRef.current));
      return true;
    });
  }, []);

  const { panelStyle } = useAnchoredPanel({
    isOpen,
    onClose: closeMenu,
    triggerRef,
    panelRef,
    placement: "below",
    align: "start",
    gap: 4,
    focusMode: "listbox",
  });

  const selectedSingle =
    props.mode === "multiple"
      ? undefined
      : (options.find((option) => Object.is(option.value, props.value)) ??
        options[0]);
  const isActive =
    props.mode === "multiple"
      ? props.value.length > 0
      : !Object.is(props.value, props.defaultValue);

  let chipLabel = name;
  if (props.mode === "multiple") {
    if (props.value.length === 1) {
      chipLabel =
        options.find((option) => Object.is(option.value, props.value[0]))
          ?.label ?? String(props.value[0]);
    } else if (props.value.length > 1) {
      chipLabel = props.countLabel(props.value.length);
    }
  } else if (isActive && selectedSingle) {
    chipLabel = selectedSingle.label;
  }

  const optionsHaveVisual = options.some(
    (option) =>
      option.icon !== undefined ||
      option.statusDot !== undefined ||
      option.dotClassName !== undefined,
  );

  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleMenu}
        className={cn(
          chipClass(isActive),
          "inline-flex max-w-[14rem] items-center gap-1.5",
          triggerClassName,
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? panelId : undefined}
        aria-label={name}
      >
        {Icon ? (
          <Icon
            className="h-3.5 w-3.5 shrink-0"
            aria-hidden
          />
        ) : null}
        {isActive && selectedSingle ? (
          <FilterOptionVisual
            option={selectedSingle}
            compact
          />
        ) : null}
        <TruncatedText>{chipLabel}</TruncatedText>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 transition-transform",
            isOpen && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            id={panelId}
            ref={panelRef}
            style={panelStyle}
            {...(modalPortalOwnerId
              ? { "data-modal-portal": modalPortalOwnerId }
              : {})}
            className="z-[200] max-h-72 min-w-[15rem] animate-scale-in overflow-y-auto rounded-xl border border-surface-200 bg-surface-0 p-1 shadow-popover"
            role="listbox"
            aria-label={name}
            aria-multiselectable={
              props.mode === "multiple" ? true : undefined
            }
          >
            {props.mode === "multiple" ? (
              <>
                <button
                  type="button"
                  role="option"
                  aria-selected={props.value.length === 0}
                  onClick={() => props.onChange([])}
                  className={cn(
                    optionRowClass(props.value.length === 0),
                    "justify-between",
                  )}
                >
                  <span>{props.anyLabel}</span>
                  {props.value.length === 0 ? (
                    <Check
                      className="h-4 w-4 shrink-0 text-primary-600"
                      aria-hidden
                    />
                  ) : null}
                </button>
                {options.length > 0 ? (
                  <div
                    className="my-1 border-t border-surface-100"
                    aria-hidden
                  />
                ) : null}
              </>
            ) : null}
            {options.map((option) => {
              const isSelected =
                props.mode === "multiple"
                  ? isValueSelected(props.value, option.value)
                  : Object.is(option.value, props.value);
              return (
                <button
                  key={optionKey(option.value)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    if (props.mode === "multiple") {
                      props.onChange(
                        isValueSelected(props.value, option.value)
                          ? props.value.filter(
                              (item) => !Object.is(item, option.value),
                            )
                          : [...props.value, option.value],
                      );
                      return;
                    }
                    props.onChange(option.value);
                    closeMenu();
                  }}
                  className={optionRowClass(isSelected)}
                >
                  <FilterOptionVisual
                    option={option}
                    reserveSpace={optionsHaveVisual}
                  />
                  <TruncatedText className="flex-1">{option.label}</TruncatedText>
                  {option.count !== undefined ? (
                    <span
                      className={cn(
                        "text-xs font-semibold tabular-nums leading-none",
                        isSelected ? "text-primary-600" : "text-surface-400",
                      )}
                    >
                      {option.count}
                    </span>
                  ) : null}
                  {isSelected ? (
                    <Check
                      className="h-4 w-4 shrink-0 text-primary-600"
                      aria-hidden
                    />
                  ) : null}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
